import { createServerFn } from '@tanstack/react-start';
import { staticFunctionMiddleware } from '@tanstack/start-static-server-functions';
import * as v from 'valibot';
import {
  COLLECTION_QUERY,
  COLLECTIONS_QUERY,
  PRODUCT_QUERY,
  PRODUCTS_QUERY
} from '../storefront-api-client-queries/queries';
import { ProductCollectionSortKeys, ProductSortKeys } from '../types/types';
import { dataOrThrow, storefrontClient } from '../utils/storefront-client';

// Static server functions: executed at build time (prerendering), their results are saved as static JSON files that
// the browser fetches on client-side navigation. Results exist only for the inputs used at build time, so calls with
// a dynamic input (e.g. a handle from the URL) need .catch(nullIfNotPrerendered), see utils/static-data.ts.
// https://tanstack.com/start/latest/docs/framework/react/guide/static-server-functions

export const getCollections = createServerFn({ method: 'GET' })
  .middleware([staticFunctionMiddleware])
  .handler(async () => {
    const { collections } = dataOrThrow(
      await storefrontClient.request(COLLECTIONS_QUERY, {
        variables: { first: 4 }
      })
    );

    return collections.nodes;
  });

export const getCollection = createServerFn({ method: 'POST' })
  .validator(
    v.object({
      handle: v.string(),
      first: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 4),
      sortKey: v.optional(v.enum(ProductCollectionSortKeys)),
      reverse: v.optional(v.boolean())
    })
  )
  .middleware([staticFunctionMiddleware])
  .handler(async ({ data }) => {
    const { collection } = dataOrThrow(
      await storefrontClient.request(COLLECTION_QUERY, {
        variables: {
          handle: data.handle,
          first: data.first,
          sortKey: data.sortKey,
          reverse: data.reverse
        }
      })
    );

    return collection ?? null;
  });

export const getProducts = createServerFn({ method: 'POST' })
  .validator(
    v.object({
      first: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 24),
      sortKey: v.optional(v.enum(ProductSortKeys))
    })
  )
  .middleware([staticFunctionMiddleware])
  .handler(async ({ data }) => {
    const { products } = dataOrThrow(
      await storefrontClient.request(PRODUCTS_QUERY, {
        variables: { first: data.first, sortKey: data.sortKey }
      })
    );

    return products;
  });

export const getProduct = createServerFn({ method: 'POST' })
  .validator(v.object({ handle: v.string() }))
  .middleware([staticFunctionMiddleware])
  .handler(async ({ data }) => {
    const { product } = dataOrThrow(
      await storefrontClient.request(PRODUCT_QUERY, {
        variables: { handle: data.handle }
      })
    );

    return product ?? null;
  });
